import { Box } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LinkButton } from "../components/Button";
import { EmptyState } from "../components/UI";
import { formatCurrency, getProduct, resolveAssetUrl, type Product } from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

type ColorOption = {
  label: string;
  hex?: string;
};

export function ProductDetailPage() {
  const { productId } = useParams();
  const { addProductToCart } = useAppState();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState<ColorOption | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    getProduct(productId)
      .then((data) => {
        if (cancelled) return;
        setProduct(data);
        setSelectedColor(data.color_options?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) setError("Product could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const subtitle = product?.subtitle || "Premium 3D Model";
  const detailText = useMemo(() => {
    if (!selectedColor?.label) return "Standard Product";
    return `Color: ${selectedColor.label}`;
  }, [selectedColor]);

  if (error) {
    return (
      <EmptyState icon={<Box size={24} />} action={<LinkButton to="/products">Back To Catalog</LinkButton>}>
        {error}
      </EmptyState>
    );
  }

  if (!product) return <p className="muted">Loading product…</p>;

  return (
    <section className="product-detail-page">
      <div className="pd-breadcrumb">
        <Link className="pd-breadcrumb-link" to="/products">Products</Link>
        <span className="pd-breadcrumb-separator">/</span>
        <span className="pd-breadcrumb-current">{product.name}</span>
      </div>

      <div className="pd-main-grid">
        <div className="pd-media-column">
          {product.stl_file ? (
            <div className="product-detail-viewer">
              <Suspense fallback={<div className="product-detail-empty">Loading STL...</div>}>
                <STLViewer
                  color={selectedColor?.label || "Blue"}
                  colorHex={selectedColor?.hex}
                  url={resolveAssetUrl(product.stl_file)}
                  mode="product"
                  interactive={true}
                  autoRotate={false}
                />
              </Suspense>
            </div>
          ) : product.image ? (
            <img src={resolveAssetUrl(product.image)} alt={product.name} className="product-detail-image" />
          ) : (
            <div className="product-detail-empty">
              <Box size={54} aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="pd-info-column">
          <div className="pd-subtitle">{subtitle}</div>
          <h1 className="pd-title">{product.name}</h1>
          <p className="pd-description">{product.description || "Ready model prepared for ProtoDeccan FDM printing."}</p>
          <div className="pd-price">{formatCurrency(product.price)}</div>

          {product.color_options?.length ? (
            <div className="pd-color-section">
              <div className="pd-color-label">Color</div>
              <div className="pd-color-swatches">
                {product.color_options.map((color) => (
                  <button
                    key={color.label}
                    className={`btn-selector product-color-swatch${selectedColor?.label === color.label ? " active" : ""}`}
                    style={{ backgroundColor: color.hex || "#4D93F8" }}
                    type="button"
                    aria-label={color.label}
                    title={color.label}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="pd-qty-section">
            <div className="pd-qty-label">Quantity</div>
            <div className="pd-qty-control">
              <button type="button" className="pd-qty-btn" onClick={() => setQuantity((value) => Math.max(1, value - 1))}>-</button>
              <span className="pd-qty-value">{quantity}</span>
              <button type="button" className="pd-qty-btn" onClick={() => setQuantity((value) => value + 1)}>+</button>
            </div>
          </div>

          <div className="pd-actions">
            <button
              className="pd-add-to-cart-btn"
              type="button"
              onClick={() => addProductToCart(product, quantity, detailText, selectedColor?.label, selectedColor?.hex)}
            >
              Add to Cart
            </button>
            <div className="pd-continue-shopping-wrapper">
              <Link className="pd-continue-shopping" to="/products">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
